<template>
  <div v-if="open" class="value-thread" role="dialog" aria-modal="true" aria-labelledby="value-thread-title">
    <button class="value-thread__backdrop" type="button" aria-label="Close Value Thread" @click="$emit('close')"></button>

    <section class="value-thread__panel" @click.stop>
      <header class="value-thread__header">
        <div>
          <div class="value-thread__eyebrow">
            <span>Manual Solve case action</span>
            <span class="value-thread__badge">Experimental</span>
          </div>
          <h2 id="value-thread-title">Value Thread</h2>
          <p>Follow economic value through swaps, bridges, and asset changes.</p>
        </div>
        <button class="value-thread__close" type="button" aria-label="Close Value Thread" :disabled="applying" @click="$emit('close')">×</button>
      </header>

      <div class="value-thread__content">
        <section class="value-thread__anchor-card">
          <div class="value-thread__section-heading">
            <div>
              <span class="value-thread__kicker">Pinned occurrence</span>
              <h3>This investigation will not follow later graph selections</h3>
            </div>
            <span class="value-thread__pin">Pinned</span>
          </div>

          <dl class="value-thread__facts">
            <div><dt>Path</dt><dd>{{ display(anchor.pathLabel) }}</dd></div>
            <div><dt>Direction</dt><dd>{{ directionLabel }}</dd></div>
            <div><dt>Main Data row</dt><dd>{{ display(anchor.rowNumber) }}</dd></div>
            <div><dt>Chain</dt><dd>{{ chainLabel(anchor) }}</dd></div>
            <div class="value-thread__fact-wide"><dt>Transaction</dt><dd class="value-thread__mono">{{ display(anchor.transactionHash) }}</dd></div>
            <div class="value-thread__fact-wide"><dt>Selected endpoint</dt><dd class="value-thread__mono">{{ display(anchor.endpointAddress) }}</dd></div>
            <div class="value-thread__fact-wide"><dt>Counterparty</dt><dd class="value-thread__mono">{{ display(anchor.counterpartyAddress) }}</dd></div>
            <div><dt>UTC date</dt><dd>{{ display(anchor.utcDate) }}</dd></div>
            <div><dt>Economic clue</dt><dd>{{ economicClue }}</dd></div>
            <div><dt>Funds Followed</dt><dd>{{ display(anchor.fundsFollowed) }}</dd></div>
            <div><dt>Continues As</dt><dd>{{ display(anchor.continuesAs) }}</dd></div>
          </dl>
        </section>

        <section class="value-thread__scope-card">
          <div class="value-thread__section-heading">
            <div>
              <span class="value-thread__kicker">Bounded search</span>
              <h3>Choose what Value Thread may investigate</h3>
            </div>
            <span class="value-thread__budget">At most {{ maximumProviderCalls }} provider calls</span>
          </div>

          <div class="value-thread__scope-grid">
            <label class="value-thread__toggle">
              <input :checked="options.analyzeSwaps !== false" type="checkbox" :disabled="busy || applying" @change="updateOption('analyzeSwaps', $event.target.checked)">
              <span><strong>Swaps</strong><small>Same-transaction input and output assets</small></span>
            </label>
            <label class="value-thread__toggle">
              <input :checked="options.analyzeBridges !== false" type="checkbox" :disabled="busy || applying" @change="updateOption('analyzeBridges', $event.target.checked)">
              <span><strong>Bridges</strong><small>Bounded source and destination correlation</small></span>
            </label>
            <label class="value-thread__toggle">
              <input :checked="options.analyzeWrapUnwrap !== false" type="checkbox" :disabled="busy || applying" @change="updateOption('analyzeWrapUnwrap', $event.target.checked)">
              <span><strong>Wrap / unwrap</strong><small>Native and wrapped-token transformations</small></span>
            </label>
          </div>

          <div class="value-thread__scope-fields">
            <label>
              <span>Bridge correlation window</span>
              <select :value="options.bridgeWindowHours || 24" :disabled="busy || applying || options.analyzeBridges === false" @change="updateOption('bridgeWindowHours', Number($event.target.value))">
                <option :value="6">6 hours</option>
                <option :value="12">12 hours</option>
                <option :value="24">24 hours</option>
                <option :value="48">48 hours</option>
                <option :value="72">72 hours</option>
              </select>
            </label>
            <label class="value-thread__checkbox-line">
              <input :checked="options.preferCache !== false" type="checkbox" :disabled="busy || applying || options.forceRefresh === true" @change="updateOption('preferCache', $event.target.checked)">
              Prefer cached immutable evidence
            </label>
            <label class="value-thread__checkbox-line">
              <input :checked="options.forceRefresh === true" type="checkbox" :disabled="busy || applying" @change="updateOption('forceRefresh', $event.target.checked)">
              Force provider refresh <small>(stricter rate limit)</small>
            </label>
          </div>

          <div v-if="error" class="value-thread__message value-thread__message--error" role="alert">{{ error }}</div>
          <div v-else-if="statusMessage" class="value-thread__message" aria-live="polite">{{ statusMessage }}</div>

          <div class="value-thread__primary-row">
            <p>No Arkham request is made until this button is pressed. Analysis never writes case data.</p>
            <button class="value-thread__primary" type="button" :disabled="!canAnalyze || busy || applying" @click="$emit('analyze')">
              <span v-if="busy" class="value-thread__spinner" aria-hidden="true"></span>
              {{ busy ? 'Analyzing value continuity…' : 'Analyze value continuity' }}
            </button>
          </div>
        </section>

        <section v-if="result" class="value-thread__results">
          <div class="value-thread__results-summary">
            <div>
              <span class="value-thread__kicker">Investigation result</span>
              <h3>{{ resultHeading }}</h3>
              <p>{{ searchSummary }}</p>
            </div>
            <div class="value-thread__request-block">
              <span>Request</span>
              <code>{{ display(result.requestId) }}</code>
            </div>
          </div>

          <div v-if="warnings.length" class="value-thread__warnings">
            <strong>Provider and search notes</strong>
            <ul><li v-for="warning in warnings" :key="warning">{{ warning }}</li></ul>
          </div>

          <div v-if="candidates.length" class="value-thread__candidate-layout">
            <aside class="value-thread__candidate-list" aria-label="Continuity hypotheses">
              <button
                v-for="candidate in candidates"
                :key="candidate.candidateId"
                type="button"
                class="value-thread__candidate"
                :class="{ 'value-thread__candidate--selected': candidate.candidateId === selectedCandidateId }"
                :disabled="busy || applying"
                @click="$emit('select-candidate', candidate.candidateId)"
              >
                <span class="value-thread__candidate-topline">
                  <strong>{{ candidate.status || 'Candidate' }}</strong>
                  <span>{{ score(candidate) }}/100</span>
                </span>
                <span class="value-thread__candidate-kind">{{ candidateLabel(candidate) }}</span>
                <small>Ranking score, not probability</small>
                <span v-if="!candidate.canApply" class="value-thread__candidate-gate">Preview only</span>
              </button>
            </aside>

            <article v-if="selectedCandidate" class="value-thread__candidate-detail">
              <div class="value-thread__candidate-title">
                <div>
                  <span class="value-thread__kicker">Selected hypothesis</span>
                  <h3>{{ candidateLabel(selectedCandidate) }}</h3>
                  <p>{{ selectedCandidate.status }} · score {{ score(selectedCandidate) }}/100, not a probability</p>
                </div>
                <span class="value-thread__apply-state" :class="{ 'value-thread__apply-state--blocked': !selectedCandidate.canApply }">
                  {{ selectedCandidate.canApply ? 'Eligible for row preview' : 'Apply blocked by evidence gate' }}
                </span>
              </div>

              <div class="value-thread__event-pair">
                <section>
                  <span>Earlier economic event</span>
                  <h4>{{ eventAsset(earlierEvent) }}</h4>
                  <dl>
                    <div><dt>Chain</dt><dd>{{ chainLabel(earlierEvent) }}</dd></div>
                    <div><dt>UTC</dt><dd>{{ display(earlierEvent.timestamp) }}</dd></div>
                    <div><dt>Hash</dt><dd class="value-thread__mono">{{ display(earlierEvent.transactionHash) }}</dd></div>
                    <div><dt>From</dt><dd class="value-thread__mono">{{ display(earlierEvent.fromAddress) }}</dd></div>
                    <div><dt>To</dt><dd class="value-thread__mono">{{ display(earlierEvent.toAddress) }}</dd></div>
                    <div><dt>Historical USD</dt><dd>{{ money(earlierEvent.historicalUsdString) }}</dd></div>
                  </dl>
                </section>
                <div class="value-thread__continuity-arrow" aria-hidden="true">→</div>
                <section>
                  <span>Later economic event</span>
                  <h4>{{ eventAsset(laterEvent) }}</h4>
                  <dl>
                    <div><dt>Chain</dt><dd>{{ chainLabel(laterEvent) }}</dd></div>
                    <div><dt>UTC</dt><dd>{{ display(laterEvent.timestamp) }}</dd></div>
                    <div><dt>Hash</dt><dd class="value-thread__mono">{{ display(laterEvent.transactionHash) }}</dd></div>
                    <div><dt>From</dt><dd class="value-thread__mono">{{ display(laterEvent.fromAddress) }}</dd></div>
                    <div><dt>To</dt><dd class="value-thread__mono">{{ display(laterEvent.toAddress) }}</dd></div>
                    <div><dt>Historical USD</dt><dd>{{ money(laterEvent.historicalUsdString) }}</dd></div>
                  </dl>
                </section>
              </div>

              <div class="value-thread__comparison-grid">
                <section class="value-thread__evidence value-thread__evidence--match">
                  <h4>Supporting evidence</h4>
                  <ul><li v-for="item in evidenceMatches" :key="item">{{ item }}</li><li v-if="!evidenceMatches.length">No positive evidence was recorded.</li></ul>
                </section>
                <section class="value-thread__evidence value-thread__evidence--conflict">
                  <h4>Conflicts</h4>
                  <ul><li v-for="item in evidenceConflicts" :key="item">{{ item }}</li><li v-if="!evidenceConflicts.length">No explicit conflict was found.</li></ul>
                </section>
                <section class="value-thread__evidence">
                  <h4>Unknown or missing</h4>
                  <ul><li v-for="item in unknownAndMissing" :key="item">{{ item }}</li><li v-if="!unknownAndMissing.length">No material evidence gap was recorded.</li></ul>
                </section>
                <section class="value-thread__evidence">
                  <h4>Score explanation</h4>
                  <ul>
                    <li v-for="part in scoreBreakdown" :key="`${part.code}-${part.points}`"><strong>{{ signed(part.points) }}</strong> {{ part.label }}</li>
                  </ul>
                  <p>Total: {{ scoreBreakdownTotal }}/100.</p>
                </section>
              </div>

              <div class="value-thread__copy-row">
                <button type="button" @click="$emit('copy', 'candidate-summary')">Copy candidate summary</button>
                <button type="button" :disabled="!earlierEvent.transactionHash" @click="$emit('copy', 'earlier-hash')">Copy earlier hash</button>
                <button type="button" :disabled="!laterEvent.transactionHash" @click="$emit('copy', 'later-hash')">Copy later hash</button>
                <button type="button" @click="$emit('copy', 'evidence')">Copy evidence summary</button>
                <button type="button" @click="$emit('copy', 'diagnostics')">Copy sanitized diagnostics</button>
              </div>

              <div v-if="candidateWarnings.length" class="value-thread__warnings value-thread__warnings--candidate">
                <strong>Candidate cautions</strong>
                <ul><li v-for="warning in candidateWarnings" :key="warning">{{ warning }}</li></ul>
              </div>

              <div class="value-thread__preview-action">
                <div>
                  <strong>Case Buddy row preview</strong>
                  <p>Build the exact 24-column rows before any Main or Raw Data mutation.</p>
                </div>
                <button type="button" :disabled="busy || applying || !selectedCandidate.canApply" @click="$emit('build-preview')">Build exact row preview</button>
              </div>
            </article>

            <article v-else class="value-thread__empty-selection">
              <strong>Select a continuity hypothesis</strong>
              <p>Value Thread does not automatically choose the highest-ranked result.</p>
            </article>
          </div>

          <div v-else class="value-thread__no-result">
            <strong>No evidence-backed continuation was found in this bounded search.</strong>
            <p>The case was not changed. You can adjust the scope, inspect the provider notes, or continue with the existing Manual Solve tools.</p>
          </div>
        </section>

        <section v-if="previewRows.length" class="value-thread__preview-card">
          <div class="value-thread__section-heading">
            <div>
              <span class="value-thread__kicker">Review buffer</span>
              <h3>Exact Case Buddy rows that will be written</h3>
              <p>All {{ previewHeaders.length }} internal columns are shown. Analysis and selection alone have not changed the spreadsheet.</p>
            </div>
            <span class="value-thread__pin">{{ previewRows.length }} row{{ previewRows.length === 1 ? '' : 's' }}</span>
          </div>

          <div class="value-thread__table-wrap">
            <table>
              <thead><tr><th v-for="(header, index) in previewHeaders" :key="`${header}-${index}`">{{ columnLetter(index) }}<span>{{ header || '(blank)' }}</span></th></tr></thead>
              <tbody><tr v-for="(row, rowIndex) in previewRows" :key="`preview-${rowIndex}`"><td v-for="(cell, index) in row" :key="`preview-${rowIndex}-${index}`">{{ cell }}</td></tr></tbody>
            </table>
          </div>

          <label class="value-thread__acknowledgement">
            <input :checked="reviewAcknowledged" type="checkbox" :disabled="busy || applying" @change="$emit('update-acknowledged', $event.target.checked)">
            <span><strong>I reviewed the evidence and the exact Case Buddy rows.</strong><small>I understand the ranking score is not a probability and manual validation remains recommended.</small></span>
          </label>

          <div class="value-thread__apply-row">
            <p>Apply revalidates the pinned occurrence, writes only this occurrence, refreshes the visual, and leaves Auto off.</p>
            <button class="value-thread__apply" type="button" :disabled="!canApply || busy || applying" @click="$emit('apply')">
              <span v-if="applying" class="value-thread__spinner" aria-hidden="true"></span>
              {{ applying ? 'Applying continuation…' : 'Apply selected continuation' }}
            </button>
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'CaseBuddyValueThreadPanel',
  props: {
    open: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    applying: { type: Boolean, default: false },
    status: { type: String, default: 'idle' },
    error: { type: String, default: '' },
    anchor: { type: Object, default: () => ({}) },
    options: { type: Object, default: () => ({}) },
    result: { type: Object, default: null },
    candidates: { type: Array, default: () => [] },
    selectedCandidateId: { type: String, default: '' },
    previewRows: { type: Array, default: () => [] },
    previewHeaders: { type: Array, default: () => [] },
    reviewAcknowledged: { type: Boolean, default: false },
    canAnalyze: { type: Boolean, default: false },
    canApply: { type: Boolean, default: false },
    maximumProviderCalls: { type: Number, default: 8 },
    disabledReason: { type: String, default: '' }
  },
  emits: [
    'close',
    'analyze',
    'select-candidate',
    'build-preview',
    'apply',
    'update-options',
    'update-acknowledged',
    'copy'
  ],
  computed: {
    selectedCandidate() {
      return this.candidates.find(candidate => candidate?.candidateId === this.selectedCandidateId) || null;
    },
    directionLabel() {
      return String(this.anchor?.direction || '').toLowerCase() === 'reverse' ? 'Reverse' : 'Forward';
    },
    economicClue() {
      return [this.anchor?.economicAmount || this.anchor?.amount, this.anchor?.economicToken || this.anchor?.token].filter(Boolean).join(' ') || '—';
    },
    statusMessage() {
      if (this.busy) return 'Arkham evidence is being gathered within the displayed provider-call ceiling.';
      if (this.applying) return 'The pinned occurrence is being revalidated before the reviewed rows are written.';
      if (this.status === 'applied') return 'The selected continuation was applied. Auto remains off.';
      if (this.disabledReason) return this.disabledReason;
      return '';
    },
    resultHeading() {
      const count = this.candidates.length;
      if (!count) return 'No evidence-backed continuation was found';
      const bridgeCandidates = this.candidates.filter(candidate => candidate?.kind === 'cross_chain_bridge');
      const unresolvedBridge = bridgeCandidates.length > 0 && bridgeCandidates.every(candidate => (
        candidate?.bridgeEvidence?.destinationMissing === true
          || candidate?.bridgeEvidence?.sourceMissing === true
          || !this.continuationHash(candidate)
      ));
      if (count === 1 && unresolvedBridge) return 'Bridge initiation found; destination still unresolved';
      if (count === 1 && bridgeCandidates.length === 1) return '1 bridge continuity hypothesis';
      if (bridgeCandidates.length === count) return `${count} bridge continuity hypotheses`;
      return `${count} ranked continuit${count === 1 ? 'y hypothesis' : 'y hypotheses'}`;
    },
    searchSummary() {
      const search = this.result?.search || {};
      const calls = Number(search.providerCallCount) || 0;
      const hits = Number(search.cacheHits) || 0;
      const state = search.searchComplete !== false
        ? 'bounded search complete'
        : this.searchReasonLabel(search.truncationReason);
      return `${calls} provider call${calls === 1 ? '' : 's'} · ${hits} cache hit${hits === 1 ? '' : 's'} · ${state}`;
    },
    warnings() {
      return Array.isArray(this.result?.warnings) ? this.result.warnings.filter(Boolean) : [];
    },
    earlierEvent() {
      return this.selectedCandidate?.earlierEvent || this.selectedCandidate?.sourceEvent || {};
    },
    laterEvent() {
      return this.selectedCandidate?.laterEvent || this.selectedCandidate?.destinationEvent || {};
    },
    evidenceMatches() {
      return Array.isArray(this.selectedCandidate?.evidenceMatches) ? this.selectedCandidate.evidenceMatches : [];
    },
    evidenceConflicts() {
      return Array.isArray(this.selectedCandidate?.evidenceConflicts) ? this.selectedCandidate.evidenceConflicts : [];
    },
    unknownAndMissing() {
      return [
        ...(Array.isArray(this.selectedCandidate?.unknownEvidence) ? this.selectedCandidate.unknownEvidence : []),
        ...(Array.isArray(this.selectedCandidate?.missingEvidence) ? this.selectedCandidate.missingEvidence : [])
      ];
    },
    scoreBreakdown() {
      return Array.isArray(this.selectedCandidate?.scoreBreakdown) ? this.selectedCandidate.scoreBreakdown : [];
    },
    scoreBreakdownTotal() {
      return this.scoreBreakdown.reduce((sum, item) => sum + (Number(item?.points) || 0), 0);
    },
    candidateWarnings() {
      return [
        ...(Array.isArray(this.selectedCandidate?.warnings) ? this.selectedCandidate.warnings : []),
        ...(!this.selectedCandidate?.canApply && Array.isArray(this.selectedCandidate?.canApplyReasons) ? this.selectedCandidate.canApplyReasons : [])
      ].filter(Boolean);
    }
  },
  methods: {
    updateOption(key, value) {
      this.$emit('update-options', { ...this.options, [key]: value });
    },
    display(value) {
      if (value === null || value === undefined || String(value).trim() === '') return '—';
      return String(value);
    },
    chainLabel(value) {
      return this.display(value?.chainName || value?.chain || value?.chainId);
    },
    candidateKind(kind) {
      return ({
        same_chain_swap: 'Same-chain swap',
        cross_chain_bridge: 'Cross-chain bridge',
        wrap_unwrap: 'Wrap / unwrap transformation',
        same_transaction_asset_change: 'Same-transaction asset change',
        multi_transfer_continuation: 'Multi-transfer continuation',
        unsupported_or_insufficient: 'Unsupported or insufficient evidence'
      })[kind] || 'Value-continuity candidate';
    },
    continuationHash(candidate = {}) {
      const direction = String(candidate?.direction || candidate?.anchorDirection || '').toLowerCase();
      const event = direction === 'reverse'
        ? (candidate?.earlierEvent || candidate?.sourceEvent || {})
        : (candidate?.laterEvent || candidate?.destinationEvent || {});
      return String(event?.transactionHash || '').trim();
    },
    candidateLabel(candidate = {}) {
      if (candidate?.kind === 'cross_chain_bridge') {
        if (candidate?.bridgeEvidence?.destinationMissing === true) return 'Bridge destination unresolved';
        if (candidate?.bridgeEvidence?.sourceMissing === true) return 'Bridge source unresolved';
        if (candidate?.providerProvenance?.directBridgeEvidence === true) return 'Direct bridge relationship';
      }
      return this.candidateKind(candidate?.kind);
    },
    searchReasonLabel(reason) {
      return ({
        bridge_beneficiary_search_truncated: 'destination-side bridge search returned more matches than the bounded page',
        provider_call_budget_reached: 'provider-call ceiling reached',
        provider_stage_unavailable: 'one or more provider stages were unavailable',
        provider_result_limit_reached: 'provider result page was truncated',
        bridge_destination_not_found: 'bridge destination was not found in the bounded window'
      })[String(reason || '')] || 'bounded search needs review';
    },
    score(candidate) {
      return Math.max(0, Math.min(100, Number(candidate?.score) || 0));
    },
    eventAsset(event) {
      const amount = event?.exactQuantityString || event?.decimalAmountString || event?.displayAmount || '';
      const token = event?.tokenSymbol || event?.tokenId || event?.tokenAddress || '';
      return [amount, token].filter(Boolean).join(' ') || 'Value details unavailable';
    },
    money(value) {
      return value === null || value === undefined || String(value).trim() === '' ? '—' : `$${String(value)}`;
    },
    signed(value) {
      const number = Number(value) || 0;
      return number > 0 ? `+${number}` : String(number);
    },
    columnLetter(index) {
      let number = index + 1;
      let label = '';
      while (number > 0) {
        number -= 1;
        label = String.fromCharCode(65 + (number % 26)) + label;
        number = Math.floor(number / 26);
      }
      return label;
    }
  }
};
</script>

<style scoped>
.value-thread { position: fixed; inset: 0; z-index: 2147482200; display: grid; place-items: center; padding: clamp(12px, 2.5vw, 34px); }
.value-thread__backdrop { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; background: rgba(6, 11, 20, .72); backdrop-filter: blur(7px); cursor: default; }
.value-thread__panel { position: relative; width: min(1220px, 100%); max-height: calc(100vh - clamp(24px, 5vw, 68px)); display: flex; flex-direction: column; overflow: hidden; color: #e9f2ff; background: radial-gradient(circle at 10% 0%, rgba(43, 120, 180, .25), transparent 32%), linear-gradient(145deg, #0a1321, #101b2d 60%, #121725); border: 1px solid rgba(121, 191, 235, .42); border-radius: 22px; box-shadow: 0 28px 90px rgba(0, 0, 0, .58), 0 0 0 1px rgba(255,255,255,.03) inset; }
.value-thread__header { display: flex; justify-content: space-between; gap: 24px; padding: 24px 28px 20px; border-bottom: 1px solid rgba(148, 187, 220, .17); background: rgba(7, 15, 27, .72); }
.value-thread__header h2 { margin: 5px 0 4px; font-size: clamp(25px, 3vw, 38px); letter-spacing: -.035em; }
.value-thread__header p { margin: 0; color: #adc0d4; }
.value-thread__eyebrow { display: flex; align-items: center; gap: 10px; color: #88caf1; font-size: 12px; font-weight: 800; letter-spacing: .11em; text-transform: uppercase; }
.value-thread__badge, .value-thread__pin, .value-thread__budget { display: inline-flex; align-items: center; min-height: 25px; padding: 3px 9px; border: 1px solid rgba(110, 209, 255, .3); border-radius: 999px; color: #c8eeff; background: rgba(36, 135, 184, .17); font-size: 11px; font-weight: 800; letter-spacing: .04em; }
.value-thread__close { width: 38px; height: 38px; border: 1px solid rgba(183, 209, 231, .2); border-radius: 12px; color: #dcecff; background: rgba(255,255,255,.045); font-size: 27px; line-height: 1; cursor: pointer; }
.value-thread__content { overflow: auto; padding: 22px 28px 30px; scrollbar-color: rgba(115, 188, 229, .5) transparent; }
.value-thread__anchor-card, .value-thread__scope-card, .value-thread__results, .value-thread__preview-card { margin-bottom: 18px; padding: 20px; border: 1px solid rgba(137, 180, 215, .17); border-radius: 16px; background: rgba(7, 16, 28, .6); }
.value-thread__section-heading, .value-thread__results-summary, .value-thread__candidate-title, .value-thread__preview-action, .value-thread__apply-row, .value-thread__primary-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.value-thread__section-heading h3, .value-thread__results-summary h3, .value-thread__candidate-title h3 { margin: 3px 0 0; font-size: 17px; }
.value-thread__section-heading p, .value-thread__candidate-title p { margin: 5px 0 0; color: #9db0c5; font-size: 13px; }
.value-thread__kicker { color: #6fc9f2; font-size: 10px; font-weight: 900; letter-spacing: .14em; text-transform: uppercase; }
.value-thread__facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin: 17px 0 0; }
.value-thread__facts > div, .value-thread__event-pair section { min-width: 0; padding: 11px 12px; border-radius: 11px; background: rgba(255,255,255,.035); }
.value-thread__fact-wide { grid-column: span 2; }
.value-thread dt, .value-thread__event-pair span { margin-bottom: 4px; color: #7f94aa; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.value-thread dd { margin: 0; overflow-wrap: anywhere; color: #edf6ff; font-size: 13px; }
.value-thread__mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 11px !important; }
.value-thread__scope-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 17px; }
.value-thread__toggle { display: flex; align-items: flex-start; gap: 10px; padding: 13px; border: 1px solid rgba(140, 187, 219, .16); border-radius: 12px; background: rgba(255,255,255,.03); }
.value-thread__toggle input, .value-thread__checkbox-line input, .value-thread__acknowledgement input { margin-top: 3px; accent-color: #3bb7f0; }
.value-thread__toggle span { display: grid; gap: 3px; }
.value-thread__toggle small, .value-thread__acknowledgement small { color: #8fa5bb; }
.value-thread__scope-fields { display: flex; flex-wrap: wrap; align-items: center; gap: 15px 22px; margin-top: 14px; color: #bed0e0; font-size: 12px; }
.value-thread__scope-fields > label:first-child { display: flex; align-items: center; gap: 9px; }
.value-thread select { min-height: 34px; padding: 4px 30px 4px 9px; border: 1px solid rgba(151, 194, 223, .25); border-radius: 8px; color: #ecf7ff; background: #122238; }
.value-thread__checkbox-line { display: inline-flex; align-items: center; gap: 7px; }
.value-thread__message { margin-top: 14px; padding: 10px 12px; border-radius: 10px; color: #c9e9fb; background: rgba(30, 116, 160, .18); }
.value-thread__message--error { color: #ffd7dc; background: rgba(190, 50, 70, .18); border: 1px solid rgba(239, 91, 112, .25); }
.value-thread__primary-row { align-items: center; margin-top: 16px; padding-top: 15px; border-top: 1px solid rgba(132, 176, 210, .12); }
.value-thread__primary-row p, .value-thread__apply-row p, .value-thread__preview-action p { margin: 0; color: #91a8bd; font-size: 12px; }
.value-thread__primary, .value-thread__apply { min-height: 42px; flex: 0 0 auto; padding: 9px 17px; border: 1px solid rgba(87, 211, 255, .55); border-radius: 11px; color: #06111b; background: linear-gradient(135deg, #83dbff, #3ca7e5); font-weight: 900; cursor: pointer; }
.value-thread button:disabled { opacity: .46; cursor: not-allowed; }
.value-thread__spinner { display: inline-block; width: 13px; height: 13px; margin-right: 7px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; vertical-align: -2px; animation: value-thread-spin .8s linear infinite; }
.value-thread__request-block { display: grid; gap: 3px; max-width: 340px; text-align: right; }
.value-thread__request-block span { color: #7188a0; font-size: 10px; text-transform: uppercase; }
.value-thread__request-block code { overflow-wrap: anywhere; color: #aaddf6; font-size: 11px; }
.value-thread__warnings { margin-top: 14px; padding: 12px 14px; border-left: 3px solid #d7a83f; border-radius: 8px; color: #f0dda8; background: rgba(145, 101, 16, .15); }
.value-thread__warnings ul, .value-thread__evidence ul { margin: 7px 0 0; padding-left: 18px; }
.value-thread__candidate-layout { display: grid; grid-template-columns: 270px minmax(0, 1fr); gap: 16px; margin-top: 17px; }
.value-thread__candidate-list { display: grid; align-content: start; gap: 9px; }
.value-thread__candidate { position: relative; display: grid; gap: 5px; width: 100%; padding: 12px; border: 1px solid rgba(141, 181, 213, .17); border-radius: 11px; color: #dcebf7; background: rgba(255,255,255,.025); text-align: left; cursor: pointer; }
.value-thread__candidate--selected { border-color: rgba(73, 192, 244, .78); background: rgba(44, 139, 187, .17); box-shadow: 0 0 0 1px rgba(69, 187, 235, .15) inset; }
.value-thread__candidate-topline { display: flex; justify-content: space-between; gap: 9px; }
.value-thread__candidate-topline > span { color: #71d1fa; font-weight: 900; }
.value-thread__candidate-kind { color: #aabed0; font-size: 12px; }
.value-thread__candidate small { color: #71879e; }
.value-thread__candidate-gate { justify-self: start; padding: 2px 7px; border-radius: 99px; color: #efc0c8; background: rgba(172, 54, 74, .18); font-size: 10px; }
.value-thread__candidate-detail, .value-thread__empty-selection { min-width: 0; padding: 16px; border: 1px solid rgba(133, 179, 211, .13); border-radius: 13px; background: rgba(0,0,0,.12); }
.value-thread__apply-state { padding: 6px 9px; border-radius: 8px; color: #aef0ca; background: rgba(41, 152, 92, .17); font-size: 11px; font-weight: 800; text-align: center; }
.value-thread__apply-state--blocked { color: #f3bec6; background: rgba(171, 56, 74, .17); }
.value-thread__event-pair { display: grid; grid-template-columns: minmax(0, 1fr) 36px minmax(0, 1fr); align-items: center; gap: 10px; margin-top: 15px; }
.value-thread__event-pair h4 { margin: 5px 0 12px; color: #bceaff; }
.value-thread__event-pair dl { display: grid; gap: 8px; margin: 0; }
.value-thread__event-pair dl > div { display: grid; gap: 2px; }
.value-thread__continuity-arrow { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 50%; color: #88dcff; background: rgba(66, 169, 216, .17); font-size: 20px; }
.value-thread__comparison-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 13px; }
.value-thread__evidence { padding: 12px; border-radius: 10px; background: rgba(255,255,255,.025); }
.value-thread__evidence h4 { margin: 0; color: #c9d9e7; font-size: 12px; }
.value-thread__evidence li, .value-thread__evidence p { color: #9eb2c4; font-size: 11px; }
.value-thread__evidence--match { border-left: 2px solid #55c98d; }
.value-thread__evidence--conflict { border-left: 2px solid #e16d7c; }
.value-thread__copy-row { display: flex; flex-wrap: wrap; gap: 7px; margin-top: 13px; }
.value-thread__copy-row button, .value-thread__preview-action button { min-height: 32px; padding: 5px 10px; border: 1px solid rgba(137, 189, 224, .23); border-radius: 8px; color: #c9e8fa; background: rgba(47, 126, 170, .12); cursor: pointer; }
.value-thread__warnings--candidate { font-size: 12px; }
.value-thread__preview-action { align-items: center; margin-top: 14px; padding-top: 14px; border-top: 1px solid rgba(130, 176, 210, .12); }
.value-thread__empty-selection, .value-thread__no-result { display: grid; place-content: center; min-height: 180px; text-align: center; color: #b7c9d8; }
.value-thread__empty-selection p, .value-thread__no-result p { color: #8298ab; }
.value-thread__table-wrap { max-width: 100%; margin-top: 15px; overflow: auto; border: 1px solid rgba(135, 176, 209, .16); border-radius: 10px; }
.value-thread table { min-width: 2600px; width: max-content; border-collapse: collapse; background: rgba(4, 10, 18, .35); }
.value-thread th, .value-thread td { max-width: 240px; padding: 7px 9px; border-right: 1px solid rgba(140, 179, 209, .11); border-bottom: 1px solid rgba(140, 179, 209, .11); color: #d5e5f2; font-size: 10px; text-align: left; overflow-wrap: anywhere; }
.value-thread th { position: sticky; top: 0; z-index: 1; color: #70cef7; background: #101d2e; }
.value-thread th span { display: block; margin-top: 2px; color: #9cb1c2; font-weight: 500; }
.value-thread__acknowledgement { display: flex; align-items: flex-start; gap: 10px; margin-top: 15px; padding: 13px; border: 1px solid rgba(141, 187, 219, .16); border-radius: 11px; background: rgba(255,255,255,.026); }
.value-thread__acknowledgement span { display: grid; gap: 3px; }
.value-thread__apply-row { align-items: center; margin-top: 14px; }
.value-thread__apply { background: linear-gradient(135deg, #8df0bd, #42bf85); border-color: rgba(119, 238, 172, .58); }
@keyframes value-thread-spin { to { transform: rotate(360deg); } }
@media (max-width: 900px) {
  .value-thread__facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .value-thread__scope-grid, .value-thread__candidate-layout { grid-template-columns: 1fr; }
  .value-thread__candidate-list { grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); }
}
@media (max-width: 620px) {
  .value-thread { padding: 0; }
  .value-thread__panel { max-height: 100vh; height: 100vh; border-radius: 0; }
  .value-thread__header, .value-thread__content { padding-left: 15px; padding-right: 15px; }
  .value-thread__facts, .value-thread__comparison-grid, .value-thread__event-pair { grid-template-columns: 1fr; }
  .value-thread__fact-wide { grid-column: auto; }
  .value-thread__continuity-arrow { transform: rotate(90deg); }
  .value-thread__section-heading, .value-thread__candidate-title, .value-thread__primary-row, .value-thread__apply-row, .value-thread__preview-action { flex-direction: column; }
  .value-thread__primary, .value-thread__apply { width: 100%; }
}
</style>
